import { useEffect, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { AuthProvider, useAuth } from '@kairos/auth/react'
import * as Sentry from '@sentry/react'

import { ThemeProvider } from './contexts/theme-context'
import { queryClient } from './lib/query-client'
import { router } from './router'
import { CrashScreen } from './components/feedback/CrashScreen'

/**
 * Bridges live auth state into the module-scope router instance. Passing
 * `context` as a prop on `<RouterProvider>` merges it into the router's
 * default context for every match — this is the canonical TSR pattern.
 *
 * `router.invalidate()` re-runs `beforeLoad` / `loader` on auth flips so
 * gated routes redirect when a user signs out and unhide when they sign in.
 *
 * `isPlatformAdmin` is null until the platform-admin check resolves; routes
 * that need it should branch on `null` (loading) vs. `false` (denied).
 * The actual admin check happens against the API Worker via a query —
 * this provider only forwards the auth user; permission resolution is a
 * route-level loader concern (Phase 2 wires it).
 */
function RouterBridge(): ReactNode {
  const { user, loading, isSuperAdmin, adminCheckComplete, signOut } = useAuth()

  // Translate the auth provider's two booleans into the `isPlatformAdmin`
  // tri-state we expose on router context. While the admin check is in
  // flight (`adminCheckComplete` false) we surface `null` so route guards
  // can render a loading fallback instead of a permission-denied screen.
  const isPlatformAdmin: boolean | null = adminCheckComplete
    ? isSuperAdmin
    : null

  // Enforce admin-only access: when the check is complete and the signed-in
  // user is NOT a platform admin, sign them out, wipe local state, and send
  // them to the login page. This covers the case where a normal Kairos user
  // tries to access the admin app directly.
  useEffect(() => {
    if (!adminCheckComplete || !user || isSuperAdmin !== false) return
    void (async () => {
      await signOut()
      localStorage.clear()
      await router.navigate({ to: '/authentication/login' })
    })()
  }, [adminCheckComplete, user, isSuperAdmin, signOut])

  useEffect(() => {
    Sentry.setUser(user ? { id: user.id } : null)
    void router.invalidate()
  }, [user, loading, isPlatformAdmin])

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth: { user, loading, isPlatformAdmin },
      }}
    />
  )
}

const SentryErrorBoundary = Sentry.ErrorBoundary

export function Providers(): ReactNode {
  return (
    <SentryErrorBoundary fallback={CrashScreen}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <RouterBridge />
          </AuthProvider>
        </ThemeProvider>
        {import.meta.env.DEV ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </QueryClientProvider>
    </SentryErrorBoundary>
  )
}
